<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <h4>List of Projects</h4>
        <LayoutCompareNote hint="New Enquiry and New Quotation open in this layout. The layout everyone uses is set in Shared Enquiry Settings." />
        <Toolbar class="p-mb-4">
          <template slot="left">
            <div class="p-inputgroup">
              <Button icon="pi pi-fw pi-refresh" class="p-mr-1" @click="search" />
              <Button icon="pi pi-fw pi-cog" class="p-mr-1" @click="columnsDialog = true" />
              <InputText v-model.trim="q" placeholder="Project name or town" @keyup.enter="search" />
              <InputText v-model.trim="area" placeholder="Postcode area" style="max-width: 130px; text-transform: uppercase;" @keyup.enter="search" />
              <Button icon="pi pi-search" class="p-mr-1" @click="search" />
              <Button icon="pi pi-times p-pr-1" class="p-mr-1" @click="clear" />
            </div>
          </template>
          <template slot="right">
            <Button label="New Enquiry" icon="pi pi-plus" class="p-button-success p-mr-2" @click="$router.push({ name: 'newEnquiry', query: viewQuery })" />
          </template>
        </Toolbar>
        <div v-if="!error && !rows.length && !loading" class="pse-note info"><i class="pi pi-info-circle"></i><span>Type 3 or more letters of a project name or town, or a postcode area (e.g. DN), then search.</span></div>
        <div v-if="error" class="pse-note info"><i class="pi pi-info-circle"></i><span>{{ error }}</span></div>

        <DataTable :value="projects" dataKey="projectId" :expandedRows.sync="expandedRows" :paginator="true" :rows="25" :rowsPerPageOptions="[25, 50, 100]"
          :loading="loading" :rowHover="true" class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-datatable-customers"
          :resizableColumns="true" columnResizeMode="expand"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} to {last} of {totalRecords} Projects">
          <Column :expander="true" headerStyle="width: 3rem" />
          <Column v-for="c in shownColumns" :key="c.field" :field="c.field" :header="c.header" :sortable="true" :headerStyle="c.style">
            <template #body="s">
              <span v-if="c.field === 'name'">
                {{ s.data.name }}
                <span v-if="s.data.legacy" class="pse-chip legacy">old enquiry</span>
              </span>
              <span v-else-if="c.money">{{ money(s.data[c.field]) }}</span>
              <span v-else>{{ s.data[c.field] }}</span>
            </template>
          </Column>
          <!-- a project's jobs and their customer quotes, as the Enquiries grid expands an enquiry into its quotes -->
          <template #expansion="s">
            <div v-for="e in s.data.sharedEnquiries" :key="e.sharedEnqId" class="p-mb-3">
              <div class="pse-row">
                <strong>{{ phaseLabel(s.data.phases, e.phaseId) }}</strong>
                <span class="pse-muted">{{ e.description }}</span>
                <span class="pse-spacer"></span>
                <Button label="Open enquiry" icon="pi pi-external-link" class="p-button-sm p-button-outlined" style="width: auto;"
                  @click="$router.push({ name: 'sharedEnquiry', params: { id: e.sharedEnqId }, query: viewQuery })" />
              </div>
              <DataTable :value="e.bids" dataKey="oppId" class="p-datatable-sm p-mt-2">
                <Column field="sQuoteNo" header="Quote No" />
                <Column field="custName" header="Customer" />
                <Column field="productType" header="Product Type" />
                <Column field="branch" header="Branch" />
              </DataTable>
            </div>
            <div v-if="!s.data.sharedEnquiries.length" class="pse-muted">No enquiries on this project yet.</div>
          </template>
        </DataTable>
      </div>
    </div>
    <GridColumns :visible.sync="columnsDialog" :columns="columns" v-model="chosen" />
  </div>
</template>

<script>
import api from './sharedEnquiryApi'
import GridColumns from './GridColumns.vue'
import LayoutCompareNote from './LayoutCompareNote.vue'
import { money, phaseLabel, errorText } from './sharedEnquiryLogic'
import { viewOf } from './enquiryLayout'

const COLUMNS = [
  { field: 'name', header: 'Project' },
  { field: 'postcodeArea', header: 'Postcode Area', style: 'width: 120px;' },
  { field: 'postCode', header: 'Post Code', style: 'width: 110px;' },
  { field: 'town', header: 'City/Town' },
  { field: 'phaseList', header: 'Phases' },
  { field: 'jobCount', header: 'Enquiries', style: 'width: 100px;' },
  { field: 'quoteCount', header: 'Quotations', style: 'width: 140px;' },
  { field: 'customers', header: 'Customers' }
]

// Design B of the Projects list (Mohammed 18/10): today's browse grid look; a row expands into its jobs and customer quotes.
export default {
  name: 'ProjectsClassic',
  components: { GridColumns, LayoutCompareNote },
  data() {
    return { q: '', area: '', rows: [], loading: false, error: '', expandedRows: [], columns: COLUMNS, chosen: COLUMNS.map(c => c.field), columnsDialog: false }
  },
  computed: {
    // the review menu's ?view= is carried on to the job page and the New Enquiry form
    viewQuery() {
      const view = viewOf(this.$route && this.$route.query)
      return view ? { view } : {}
    },
    shownColumns() { return this.columns.filter(c => this.chosen.includes(c.field)) },
    projects() {
      return this.rows.map(p => {
        const jobs = p.sharedEnquiries || []
        const bids = [].concat(...jobs.map(e => e.bids || []))
        return {
          ...p, sharedEnquiries: jobs,
          phaseList: (p.phases || []).map(ph => ph.phaseNo).join(', ') || 'Main project',
          jobCount: jobs.length, quoteCount: bids.length,
          customers: [...new Set(bids.map(b => b.custName))].join(', ')
        }
      })
    }
  },
  // the server needs 3+ letters or a postcode area: nothing is searched until one is typed
  mounted() { if (this.q || this.area) this.search() },
  methods: {
    money, phaseLabel,
    async search() {
      this.loading = true
      this.error = ''
      try {
        this.rows = (await api.projects(this.q, this.area.toUpperCase())) || []
      } catch (e) {
        this.rows = []
        this.error = errorText(e, 'Could not search the projects')
      } finally {
        this.loading = false
      }
    },
    clear() { this.q = ''; this.area = ''; this.rows = []; this.error = '' }
  }
}
</script>

<style scoped>
@import './sharedEnquiry.css';
</style>
